import React, { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { wsManager } from '../api/websocket';
import { usePolling } from '../utils/poll';
import { playSound, SoundName } from '../utils/sounds';

/**
 * Звук на заливку и выливку и сообщение о новой грамоте. Ничего не рисует.
 *
 * Исполнения приходят по вебсокету сообщением `trade` (канал `alerts`, на него
 * подписывает AlertNotifications — оба компонента живут в AppShell вместе).
 * Лесенка заливается пачкой ордеров, поэтому один и тот же звук — не чаще раза
 * в 700 мс: иначе десять ступеней звучали бы пулемётом.
 */
const GAP_MS = 700;
const SEEN_KEY = 'sb_achievements_seen';

export const TradeSounds: React.FC = () => {
  const { t } = useTranslation('achievements');
  const last = useRef<Record<string, number>>({});

  useEffect(() => {
    const unsub = wsManager.subscribe('trade', (msg: unknown) => {
      const ev = (msg as { data?: { kind?: string; pnl_usd?: number | null } })?.data;
      if (!ev?.kind) return;
      let name: SoundName;
      if (ev.kind.includes('open')) name = 'enter';
      else {
        const pnl = Number(ev.pnl_usd);
        name = !Number.isFinite(pnl) || pnl === 0 ? 'exit' : pnl > 0 ? 'profit' : 'loss';
      }
      const now = Date.now();
      if (now - (last.current[name] || 0) < GAP_MS) return;
      last.current[name] = now;
      playSound(name);
    });
    return () => { unsub?.(); };
  }, []);

  // Новые грамоты: сравниваем с тем, что этот браузер уже видел. Первый заход
  // только запоминает список — иначе человек получил бы пачку поздравлений за
  // всё, что заработал до появления раздела.
  usePolling(async () => {
    try {
      const r = await apiClient.raw.get('/api/achievements');
      const got: string[] = (r.data?.items || []).filter((x: any) => x.earned).map((x: any) => String(x.id));
      let seen: string[] | null = null;
      try { seen = JSON.parse(localStorage.getItem(SEEN_KEY) || 'null'); } catch { seen = null; }
      if (Array.isArray(seen)) {
        const fresh = got.filter(id => !seen!.includes(id));
        if (fresh.length) {
          playSound('award');
          for (const id of fresh.slice(0, 3)) {
            const family = id.replace(/_\d+$/, '');
            toast.success(`🏆 ${t('newOne', { title: t(`family.${family}.title`) })}`, { duration: 8000, id: `ach-${id}` });
          }
        }
      }
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(got)); } catch { /* приватный режим */ }
    } catch { /* не вошли или нет связи — молчим */ }
  }, [], { intervalMs: 120000, initialDelayMs: 9000 });

  return null;
};

export default TradeSounds;
